import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrivateAxios } from "../../environment/AxiosInstance";
import { CurrencyList } from "../../utils/StaticData";
import { SuccessMessage, ErrorMessage } from "../../environment/ToastMessage";
import { UserAuth } from "../auth/Auth";
import moment from "moment";
import "./TargetPI.css";

const PERIODS = ["daily", "weekly", "monthly", "quarterly", "yearly"];
const cap = (p) => p.charAt(0).toUpperCase() + p.slice(1);

/**
 * Add PI Target — standalone, independent of "Add Sales Target".
 * Admins/owners can assign one target to MULTIPLE users, each with their own
 * amount and frequency (period). Regular users create a target for themselves.
 */
export default function AddPiTarget() {
  const navigate = useNavigate();
  const { MatchPermission, AdminExist, userDetails, User } = UserAuth();

  const canAdd =
    AdminExist || userDetails?.position === "Owner" || MatchPermission(["Target PI All", "Add Target PI"]);
  const isManager =
    AdminExist || userDetails?.position === "Owner" || MatchPermission(["Target PI All"]);

  const [shared, setShared] = useState({
    title: "",
    from_date: moment().format("YYYY-MM-DD"),
    to_date: moment().endOf("month").format("YYYY-MM-DD"),
    currency: "INR",
  });
  // Managers manage a list of {assignee_id, assigned_amount, period}.
  const [rows, setRows] = useState([{ assignee_id: "", assigned_amount: "", period: "monthly" }]);
  // Non-managers just have their own amount + period.
  const [selfRow, setSelfRow] = useState({ assigned_amount: "", period: "monthly" });
  const [saving, setSaving] = useState(false);

  if (!canAdd) {
    return (
      <div className="p-4 text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to add a PI target.</p>
      </div>
    );
  }

  const setS = (k) => (e) => setShared({ ...shared, [k]: e.target.value });

  const updateRow = (i, key, val) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, { assignee_id: "", assigned_amount: "", period: "monthly" }]);
  const removeRow = (i) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // Users not yet chosen in another row (keeps the dropdowns clean).
  const availableFor = (i) => {
    const taken = new Set(rows.map((r, idx) => (idx === i ? null : String(r.assignee_id))).filter(Boolean));
    return (User || []).filter((u) => !taken.has(String(u.id)));
  };

  async function save() {
    if (!shared.title.trim()) return ErrorMessage("Title is required");
    if (shared.from_date && shared.to_date && shared.to_date < shared.from_date) {
      return ErrorMessage("To date must be on or after From date");
    }

    let assignees;
    if (isManager) {
      const valid = rows.filter((r) => r.assignee_id);
      if (valid.length === 0) return ErrorMessage("Add at least one employee");
      const ids = valid.map((r) => String(r.assignee_id));
      if (new Set(ids).size !== ids.length) return ErrorMessage("The same employee is selected more than once");
      assignees = valid.map((r) => ({
        assignee_id: r.assignee_id,
        assigned_amount: r.assigned_amount || 0,
        period: r.period,
      }));
    } else {
      assignees = [
        { assignee_id: userDetails?.id, assigned_amount: selfRow.assigned_amount || 0, period: selfRow.period },
      ];
    }

    setSaving(true);
    try {
      const res = await PrivateAxios.post("/sales-pi/targets/bulk", {
        title: shared.title,
        from_date: shared.from_date || null,
        to_date: shared.to_date || null,
        currency: shared.currency,
        assignees,
      });
      SuccessMessage(res.data?.message || "PI target created");
      navigate("/sales/target-pi");
    } catch (err) {
      ErrorMessage(err.response?.data?.message || "Failed to create PI target");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container-fluid target-pi-page">
      <div className="pi-form-card" style={{ maxWidth: 920 }}>
        <div className="pi-form-card__header">
          <h5>
            <i className="fas fa-bullseye me-2"></i>Add PI Target
          </h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/sales/target-pi")}>
            <i className="fas fa-arrow-left me-1"></i> Back to Target PI
          </button>
        </div>

        <div className="pi-form-card__body">
          {/* Shared fields */}
          <div className="row g-3 mb-2">
            <div className="col-12">
              <label className="form-label">Target Title <span className="text-danger">*</span></label>
              <input className="form-control" placeholder="e.g. Q1 Sales Target" value={shared.title} onChange={setS("title")} maxLength={200} />
            </div>
            <div className="col-md-4">
              <label className="form-label">From</label>
              <input type="date" className="form-control" value={shared.from_date} onChange={setS("from_date")} />
            </div>
            <div className="col-md-4">
              <label className="form-label">To</label>
              <input type="date" className="form-control" value={shared.to_date} onChange={setS("to_date")} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Currency</label>
              <select className="form-select" value={shared.currency} onChange={setS("currency")}>
                {CurrencyList.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>
                ))}
              </select>
            </div>
          </div>

          {isManager ? (
            <>
              <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                <label className="form-label mb-0">Assign to Employees <span className="text-danger">*</span></label>
                <button className="btn btn-sm btn-outline-primary" onClick={addRow}>
                  <i className="fas fa-plus me-1"></i> Add User
                </button>
              </div>

              <div className="table-responsive">
                <table className="table table-sm align-middle pi-assign-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "40%" }}>Employee</th>
                      <th style={{ width: "28%" }}>Amount</th>
                      <th style={{ width: "26%" }}>Frequency</th>
                      <th style={{ width: 44 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td>
                          <select className="form-select form-select-sm" value={r.assignee_id} onChange={(e) => updateRow(i, "assignee_id", e.target.value)}>
                            <option value="">Select employee…</option>
                            {availableFor(i).map((u) => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm" placeholder="0" value={r.assigned_amount} onChange={(e) => updateRow(i, "assigned_amount", e.target.value)} />
                        </td>
                        <td>
                          <select className="form-select form-select-sm" value={r.period} onChange={(e) => updateRow(i, "period", e.target.value)}>
                            {PERIODS.map((p) => (<option key={p} value={p}>{cap(p)}</option>))}
                          </select>
                        </td>
                        <td className="text-center">
                          <button className="btn btn-sm btn-link text-danger p-0" title="Remove" disabled={rows.length === 1} onClick={() => removeRow(i)}>
                            <i className="fas fa-times-circle"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-text">Each user gets their own target row with their own amount and frequency.</div>
            </>
          ) : (
            <div className="row g-3 mt-1">
              <div className="col-12">
                <div className="alert alert-light border py-2 mb-0">
                  <i className="fas fa-user me-2 text-secondary"></i>
                  This target will be created for you (<strong>{userDetails?.name || "me"}</strong>).
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Assigned Amount (Target)</label>
                <input type="number" className="form-control" placeholder="0" value={selfRow.assigned_amount} onChange={(e) => setSelfRow({ ...selfRow, assigned_amount: e.target.value })} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Frequency</label>
                <select className="form-select" value={selfRow.period} onChange={(e) => setSelfRow({ ...selfRow, period: e.target.value })}>
                  {PERIODS.map((p) => (<option key={p} value={p}>{cap(p)}</option>))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="pi-form-card__footer">
          <button className="btn btn-light border" disabled={saving} onClick={() => navigate("/sales/target-pi")}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>
            {saving ? "Saving..." : "Create PI Target"}
          </button>
        </div>
      </div>
    </div>
  );
}
